import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { publicApi } from "@/lib/api";
import { buildWhatsAppLink, trackWhatsAppClick } from "@/components/FloatingWhatsApp";

const QUICK_PROMPTS = [
  {
    id: "best-time",
    label: "Best time to visit?",
    answer:
      "October to May is peak season — calm seas, sunny skies, best for snorkelling and scuba. December-January books out fastest, so we recommend confirming 4-6 weeks in advance.",
  },
  {
    id: "family",
    label: "Family package for 4?",
    answer:
      "Our Family Fun Vacation covers Port Blair + Havelock + Neil with kid-friendly activities (glass-bottom boat, sandy beaches, mild snorkelling). Would you like me to send you the full itinerary on WhatsApp?",
    whatsapp:
      "Hi! I'd like the Family Fun Vacation itinerary and pricing for 4 travellers.",
  },
  {
    id: "honeymoon",
    label: "Honeymoon idea",
    answer:
      "Our Romantic Honeymoon Escape includes a beach candlelight dinner, private ferry seats, and a stay at a sea-facing resort in Havelock. Shall I share the day-by-day plan on WhatsApp?",
    whatsapp:
      "Hi! Please share the Romantic Honeymoon Escape itinerary and next-available dates.",
  },
  {
    id: "custom",
    label: "Build a custom trip",
    answer:
      "Tell us your dates, group size and interests — we'll design a day-by-day plan. Use our Customize Tour page or tap below to chat on WhatsApp with a travel expert.",
    whatsapp:
      "Hi! I'd like to build a custom Andaman itinerary. Here are my rough dates and group size:",
  },
  {
    id: "price",
    label: "Best price today?",
    answer:
      "Prices depend on your dates and pax. Leave your number below and a travel expert will call within 2 hours with the best available quote.",
    isCallback: true,
  },
];

const STORAGE_KEY = "aa_live_chat_seen";
const AUTO_OPEN_MS = 45000; // Nudge new visitors after 45s

const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hi there! 👋 I'm your Andaman travel expert. What can I help you with today?",
    },
  ]);
  const [pendingCallback, setPendingCallback] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const bottomRef = useRef(null);

  // One-time auto-nudge on new visitors (not on admin/auth flows)
  useEffect(() => {
    const path = window.location.pathname;
    const excluded = [
      "/admin", "/login", "/register", "/forgot-password", "/reset-password",
      "/auth/callback", "/payment-success", "/payment-cancel",
    ];
    if (excluded.some((p) => path.startsWith(p))) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), AUTO_OPEN_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open) localStorage.setItem(STORAGE_KEY, "1");
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, pendingCallback]);

  const handlePrompt = (prompt) => {
    setMessages((prev) => [
      ...prev,
      { from: "user", text: prompt.label },
      { from: "bot", text: prompt.answer, prompt },
    ]);
    if (prompt.isCallback) setPendingCallback(true);
  };

  const handleWhatsApp = (prompt) => {
    trackWhatsAppClick("live_chat_widget", prompt.id);
    const message = prompt.whatsapp || `Hi! I want to know more about: ${prompt.label}`;
    window.open(buildWhatsAppLink(message), "_blank", "noopener,noreferrer");
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    try {
      await publicApi.post("/inquiries/quote", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: "Callback requested from Live Chat widget",
      });
      setSubmitted(true);
      setPendingCallback(false);
      setMessages((prev) => [
        ...prev,
        { from: "user", text: `Please call ${form.name} at ${form.phone}` },
        { from: "bot", text: "Got it! ✨ A travel expert will call you within 2 hours." },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "Couldn't submit right now — please WhatsApp us and we'll respond instantly." },
      ]);
    }
  };

  return (
    <>
      {/* Trigger button */}
      <motion.button
        onClick={() => setOpen(true)}
        data-testid="live-chat-fab"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: open ? 0 : 1, opacity: open ? 0 : 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-[168px] z-50 bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full p-4 shadow-lg group flex items-center"
        style={{ boxShadow: "0 4px 20px rgba(214, 179, 112, 0.45)" }}
        aria-label="Chat with a travel expert"
      >
        <MessageSquare size={26} />
        <span className="ml-2 max-w-0 overflow-hidden group-hover:max-w-[9rem] whitespace-nowrap font-medium transition-[max-width] duration-300 text-sm">
          Chat with expert
        </span>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            data-testid="live-chat-panel"
            className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[360px] max-h-[calc(100vh-3rem)] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-border"
          >
            {/* Header */}
            <div className="bg-primary text-primary-foreground p-4 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="text-secondary" size={16} />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-secondary font-semibold">Andaman Avalon</span>
                </div>
                <h3 className="font-serif text-lg leading-tight">Chat with a travel expert</h3>
                <p className="text-xs text-primary-foreground/70 mt-0.5">Usually replies within a few minutes</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="live-chat-close"
                aria-label="Close chat"
                className="rounded-full p-1.5 hover:bg-white/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.from === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-white shadow-sm text-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                    {m.prompt && (m.prompt.whatsapp || !m.prompt.isCallback) && (
                      <button
                        onClick={() => handleWhatsApp(m.prompt)}
                        data-testid={`live-chat-whatsapp-${m.prompt.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#25D366] hover:underline"
                      >
                        Continue on WhatsApp →
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {pendingCallback && !submitted && (
                <form
                  onSubmit={handleCallbackSubmit}
                  data-testid="live-chat-callback-form"
                  className="bg-white rounded-2xl rounded-bl-sm shadow-sm p-3 space-y-2"
                >
                  <input
                    data-testid="live-chat-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  />
                  <input
                    data-testid="live-chat-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone / WhatsApp"
                    required
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  />
                  <button
                    type="submit"
                    data-testid="live-chat-callback-submit"
                    className="w-full h-9 bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-md text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Send size={14} /> Request Callback
                  </button>
                </form>
              )}

              {submitted && (
                <div className="flex justify-start">
                  <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-2xl rounded-bl-sm text-xs flex items-center gap-1.5">
                    <Check size={14} /> Callback requested
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Quick prompts */}
            <div className="border-t border-border p-3 bg-white">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">
                Quick help
              </p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handlePrompt(p)}
                    data-testid={`live-chat-prompt-${p.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-primary/20 hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveChat;
